import { ENDPOINTS } from "@/api/application-api";
import { useUserToken } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { Pagination } from "@/types";
import {
  GetWorkspaceInvitesIn,
  WorkspaceInvite,
  WorkspaceInviteIn,
  WorkspaceInviteUpdateIn,
} from "@/types/workspace-invite";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const useCreateWorkspaceInvite = () => {
  const userToken = useUserToken();
  const queryClient = useQueryClient();
  return useMutation(
    (body: WorkspaceInviteIn): Promise<{ message: string } | WorkspaceInvite> =>
      fetch(ENDPOINTS.workspace_invites, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then((res) => res.json()),
    {
      onSuccess: (data) => {
        if ("message" in data) {
          displayErrorToast(data.message);
          return;
        }
        queryClient.invalidateQueries([ENDPOINTS.workspace_invites]);
      },
    },
  );
};

const useWorkspaceInvites = (params?: GetWorkspaceInvitesIn) => {
  const userToken = useUserToken();
  const url = new URL(ENDPOINTS.workspace_invites);
  if (params?.invitee_email)
    url.searchParams.append("invitee_email", params.invitee_email);
  if (params?.workspace_id)
    url.searchParams.append("workspace_id", params.workspace_id.toString());
  if (params?.status) url.searchParams.append("status", params.status);
  if (params?.organization_id)
    url.searchParams.append(
      "organization_id",
      params.organization_id.toString(),
    );

  return useQuery(
    [ENDPOINTS.workspace_invites, params],
    (): Promise<Pagination<WorkspaceInvite>> =>
      fetch(url, {
        headers: jsonAuthHeaders(userToken),
      }).then(handleResponse),
  );
};

const useUpdateWorkspaceInvite = () => {
  const userToken = useUserToken();
  const queryClient = useQueryClient();
  return useMutation(
    (
      body: WorkspaceInviteUpdateIn,
    ): Promise<{ message: string } | WorkspaceInvite> =>
      fetch(`${ENDPOINTS.workspace_invites}/${body.id}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handleResponse),
    {
      onSuccess: (data) => {
        if ("message" in data) {
          displayErrorToast(data.message);
          return;
        }
        queryClient.invalidateQueries([ENDPOINTS.workspace_invites]);
      },
    },
  );
};

const useDeleteWorkspaceInvite = () => {
  const userToken = useUserToken();
  const queryClient = useQueryClient();
  return useMutation(
    (id: number) =>
      fetch(`${ENDPOINTS.workspace_invites}/${id}`, {
        method: "DELETE",
        headers: jsonAuthHeaders(userToken),
      }),
    {
      onSuccess: async (response) => {
        if (response.ok) {
          displayToast(
            "Workspace invite deleted successfully",
            ToastType.success,
          );
          queryClient.invalidateQueries([ENDPOINTS.workspace_invites]);
          return;
        }
        const data = await response.json();
        if (data?.message) {
          displayErrorToast(data.message);
          return;
        }
        displayErrorToast("Failed to delete workspace invite");
      },
    },
  );
};

export {
  useCreateWorkspaceInvite,
  useDeleteWorkspaceInvite,
  useUpdateWorkspaceInvite,
  useWorkspaceInvites,
};
